/**
 * We know the specific components are located at these paths, which
 * can also be viewed like an article at the url, so we want to make sure
 * when doing an embed of the electorate components, that we strip the
 * whole web page and JUST show the components on the embed
 */
export function isElection(link: string) {
    return link.startsWith(electionsElectoratePrefix)
}

const electionsElectoratePrefix = 'embeds://elections-electorate/'

/**
 * Matches embeds that name the election by id, e.g.
 * `embeds://elections-electorate/vic-election-2026/Balcatta`
 */
const electionIdPattern = /^([a-z]+)-election-(\d{4})$/i

function matchElectionId(link: string) {
    if (!link.startsWith(electionsElectoratePrefix)) {
        return undefined
    }

    const firstSegment = link
        .slice(electionsElectoratePrefix.length)
        .split('/')[0]
    const match = firstSegment.match(electionIdPattern)

    return match
        ? {
              electionId: firstSegment.toLowerCase(),
              prefix: match[1].toLowerCase(),
              year: Number(match[2]),
          }
        : undefined
}

export type ElectionType =
    | 'federal'
    | 'state-wa'
    | 'state-qld'
    | 'state-nsw'
    | 'state-vic'
    | 'state-sa'
    | 'state-tas'
    | 'state-nt'
    | 'state-act'
export interface ElectorateEmbedData {
    type: ElectionType
    electorate: string
    year: number
    /** Set when the embed names the election by id, e.g. `vic-election-2026` */
    electionId?: string
}
export function getElectorateData(link: string): ElectorateEmbedData {
    const trimmedString = link.replace(electionsElectoratePrefix, '')
    const split = trimmedString.split('/')

    try {
        let nextIndex = 1
        const rawElectionType = split[0]
        const electionIdMatch = matchElectionId(link)
        let electionType: ElectionType

        if (electionIdMatch) {
            electionType =
                electionIdMatch.prefix === 'federal'
                    ? 'federal'
                    : (`state-${electionIdMatch.prefix}` as ElectionType)

            return {
                type: electionType,
                year: electionIdMatch.year,
                electorate: split[1],
                electionId: electionIdMatch.electionId,
            }
        }

        if (rawElectionType === 'state') {
            electionType = `state-${split[1].toLowerCase()}` as ElectionType
            nextIndex = 2
        } else {
            electionType = 'federal'
        }

        const year = Number(split[nextIndex++])
        const electorate = split[nextIndex++]

        return {
            type: electionType,
            year,
            electorate,
        }
    } catch (_) {
        throw new Error(
            `Invalid electorate link: ${link}, refer to docs/election-embeds.md for the correct format`,
        )
    }
}

export function getWanElectionId(electorateData: ElectorateEmbedData) {
    if (electorateData.electionId) {
        return electorateData.electionId
    }

    if (electorateData.type === 'federal') {
        return `federal-election-${electorateData.year}`
    }

    return `${electorateData.type.replace('state-', '')}-election-${
        electorateData.year
    }`
}
